import '../../assets/styles/auth.scss';
import logo from '../../assets/images/logo/logo2.png';
import { Link, useNavigate } from 'react-router-dom';
import { Fragment, useContext, useState } from 'react';
import loginSuccess from "../../context/actions/auth/loginSuccess";
import henceforthApi from '../../utils/henceforthApi';
import { GlobalContext, handleError } from '../../context/Provider';
import { validateEmail } from '../../utils/validations';
import Spinner from '../../components/common/spinner';
import { requestNotification } from '../../utils/firebase';
import henceofrthEnums from '../../utils/henceofrthEnums';
import { Mail, Lock, Eye, EyeOff, ArrowRight, ShieldCheck, AlertCircle, Store } from 'lucide-react';

const SignIn = ({ logOutNow }: any) => {
  const navigate = useNavigate();
  const { authDispatch, toastMessage, language_data, authState } = useContext(GlobalContext);
  const [emailErr, setEmailErr] = useState('');
  const [passwordErr, setPasswordErr] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [state, setState] = useState({
    email: "gladwin@yopmail.com",
    password: "Qwerty@1",
    loading: false,
  });

  const handleInput = (e: any) => {
    const { name, value } = e.target;
    if (name === 'email') setEmailErr('');
    if (name === 'password') setPasswordErr('');
    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onSubmitForm = async (e: any) => {
    e.preventDefault();
    let hasErr = false;

    if (!state.email.trim()) {
      setEmailErr("Email is required");
      hasErr = true;
    } else if (!validateEmail(state.email)) {
      setEmailErr("Please enter a valid email address");
      hasErr = true;
    }

    if (!state.password) {
      setPasswordErr("Password is required");
      hasErr = true;
    } else if (!state.password.trim()) {
      setPasswordErr("Please enter a valid password");
      hasErr = true;
    }

    if (hasErr) return;

    const { email, password } = state;
    let fcmid = await requestNotification();
    let items = {
      email: email.toLowerCase(),
      password: password,
      language: "ENGLISH",
    } as any;
    if (fcmid) items['fcm_token'] = fcmid;

    setState((prev) => ({ ...prev, loading: true }));
    try {
      let res = await henceforthApi.Auth.login(items);
      loginSuccess({ ...res.data, lang: henceofrthEnums.Language.ENGLISH })(authDispatch);
      henceforthApi.setToken(res.data.access_token);
      henceforthApi.languageChange(henceofrthEnums.Language.ENGLISH);
      navigate('/');
      toastMessage("Login Successfully");
    } catch (err: any) {
      handleError(err, 'active', authDispatch);
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50/40 to-slate-100 flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-hidden select-none">
      {/* Decorative background blurs */}
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#1AA7DE]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-sky-400/10 blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 p-8 sm:p-10 transition-all">
          {/* Logo & Brand Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block hover:opacity-95 transition-opacity">
              <img
                src={logo}
                alt="Ready Ecommerce"
                className="h-11 w-auto max-w-[210px] object-contain mx-auto"
              />
            </Link>
            <h1 className="mt-2 text-2xl font-black text-slate-800 tracking-tight">
              Welcome back
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Enter your credentials to manage your store
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={onSubmitForm} className="space-y-5" noValidate>
            {/* Email Field */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Mail className="w-5 h-5" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={state.email}
                  onChange={handleInput}
                  placeholder="seller@example.com"
                  className={`w-full pl-11 pr-4 py-3 bg-slate-50/70 border ${emailErr ? 'border-rose-400 bg-rose-50/30 ring-2 ring-rose-100' : 'border-slate-200 hover:border-slate-300'
                    } rounded-xl text-slate-800 placeholder-slate-400 text-sm font-medium focus:bg-white focus:border-[#1AA7DE] focus:ring-4 focus:ring-[#1AA7DE]/10 outline-none transition-all`}
                />
              </div>
              {emailErr && (
                <p className="mt-1.5 text-xs font-semibold text-rose-500 flex items-center gap-1.5 animate-fadeIn">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>{emailErr}</span>
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-semibold text-[#1AA7DE] hover:text-[#1596c9] hover:underline transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={state.password}
                  onChange={handleInput}
                  placeholder="••••••••"
                  className={`w-full pl-11 pr-11 py-3 bg-slate-50/70 border ${passwordErr ? 'border-rose-400 bg-rose-50/30 ring-2 ring-rose-100' : 'border-slate-200 hover:border-slate-300'
                    } rounded-xl text-slate-800 placeholder-slate-400 text-sm font-medium focus:bg-white focus:border-[#1AA7DE] focus:ring-4 focus:ring-[#1AA7DE]/10 outline-none transition-all`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {passwordErr && (
                <p className="mt-1.5 text-xs font-semibold text-rose-500 flex items-center gap-1.5 animate-fadeIn">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>{passwordErr}</span>
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={state.loading}
                className="w-full py-3.5 px-4 bg-[#1AA7DE] hover:bg-[#1596c9] active:scale-[0.99] text-white font-bold rounded-xl shadow-lg shadow-[#1AA7DE]/25 hover:shadow-xl hover:shadow-[#1AA7DE]/30 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed group text-base"
              >
                {state.loading ? (
                  <Spinner color="text-white" />
                ) : (
                  <>
                    <span>Sign In to Portal</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Switch to Register */}
          <div className="mt-8 pt-6 border-t border-slate-100 text-center">
            <p className="text-sm font-medium text-slate-500">
              Don't have a seller account?{' '}
              <Link
                to="/signup"
                className="font-bold text-[#1AA7DE] hover:text-[#1596c9] hover:underline transition-colors ml-1"
              >
                Register as Seller
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
